import React, { useCallback, useEffect, useRef } from 'react';
import Pride from 'react-canvas-confetti/dist/presets/pride';
import { Box } from '@mui/material';
import { PropertyField } from '../../../../system/field';
import { useListingField, ListingResource } from '@brightmls/nextgen.lae.flc';
import {
  TConductorInstance,
  TOnInitPresetFn,
} from 'react-canvas-confetti/dist/types';

interface ConfettiProps {
  fireSignal: boolean;
}

const Confetti: React.FC<ConfettiProps> = (props) => {
  const { fireSignal } = props;
  const refConductorInstance = useRef<TConductorInstance | null>(null);

  useEffect(() => {
    if (fireSignal) {
      fireConfetti();
    } else {
      pauseConfetti();
    }
  }, [fireSignal]);

  const onInitPreset: TOnInitPresetFn = useCallback(
    ({ confetti: _confetti, conductor }) => {
      // Store the conductor instance to manually control the animation
      refConductorInstance.current = conductor;
    },
    [],
  );

  // Manually trigger the animation using the conductor
  const fireConfetti = useCallback(() => {
    if (refConductorInstance.current) {
      refConductorInstance.current.run({ speed: 40 }); // 30 for Pride, 3 for fireworks, 1 for realistic
    }
  }, [fireSignal]);

  // Conductor instances can be stopped or paused. Pause allows the animation to complete before stopping
  const pauseConfetti = useCallback(() => {
    if (refConductorInstance.current) {
      refConductorInstance.current.pause();
    }
  }, []);

  const propertyTypeField = useListingField(
    ListingResource.Property,
    PropertyField.PROPERTY_TYPE,
    'Residential',
  );

  const colorSets = {
    Primary: ['#a43a19', '#eb5528', '#f1a07b', '#f9dccb', '#ffffff'],
    Secondary: ['#061aab', '#5f71f6', '#7a95f8', '#cbd2fb', '#ffffff'],
    Tertiary: ['#437970', '#a9e4db', '#cefdf6', '#d7fef8', '#fffffff'],
  };

  // sourced from enum PropertyTypeNames in property-type.ts
  const colorsByPropertyType = {
    Residential: colorSets.Secondary,
    'Commericial Sale': colorSets.Secondary,
    BusinessOpportunity: colorSets.Secondary,
    'Multi-Family': colorSets.Primary,
    'Residential Lease': colorSets.Primary,
    'Commerical Lease': colorSets.Primary,
    Farm: colorSets.Tertiary,
    Land: colorSets.Tertiary,
  };

  const decorateOptionsPride = (defaultOptions: any) => {
    return {
      ...defaultOptions,
      colors:
        colorsByPropertyType[
          propertyTypeField.value as unknown as keyof typeof colorsByPropertyType
        ],
      shapes: ['square'],
      startVelocity: 100,
      particleCount: 9,
      // 0, 0 puts it at the middle of the screen. There are two
      // conductors in the preset we're using, so we need to leave
      // x alone to let them remain on the sides of the screen
      origin: { x: defaultOptions.origin.x, y: 0.9 },
      drift: Math.random() * (1 - -1 + 1) + -1,
      gravity: 0.75,
    };
  };

  return (
    <Box sx={{ zIndex: 1000 }}>
      <Pride
        onInit={onInitPreset}
        decorateOptions={decorateOptionsPride}
        globalOptions={{
          disableForReducedMotion: true,
          resize: true,
          // use worker offloads the animation calculations to Web Worker, which improves performance and visual smoothness
          useWorker: true,
        }}
      />
    </Box>
  );
};

export default React.memo(Confetti);
